import { motion } from "framer-motion"
import { Mic, Square, Loader2 } from "lucide-react"
import { cn } from "@/lib/utils"

interface RecordingButtonProps {
  isRecording: boolean
  isTranscribing: boolean
  durationMs: number
  amplitudes: number[]
  onStart: () => void
  onStop: () => void
  disabled?: boolean
}

const MAX_MS = 60_000

function formatDuration(ms: number): string {
  const total = Math.floor(ms / 1000)
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, "0")}`
}

export function RecordingButton({
  isRecording,
  isTranscribing,
  durationMs,
  amplitudes,
  onStart,
  onStop,
  disabled,
}: RecordingButtonProps) {
  const isHot = durationMs > MAX_MS - 10_000

  if (isTranscribing) {
    return (
      <div
        className={cn(
          "flex items-center gap-2 px-3 h-9 rounded-lg",
          "bg-glass-card border border-glass-border",
          "text-xs text-[var(--text-secondary)]",
        )}
      >
        <Loader2 className="w-4 h-4 animate-spin" />
        Transcrevendo...
      </div>
    )
  }

  if (isRecording) {
    return (
      <motion.div
        layout
        className={cn(
          "flex items-center gap-2 px-2 h-9 rounded-lg",
          "bg-red-500/10 border border-red-500/30",
        )}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.15 }}
      >
        {/* Pulsing dot */}
        <motion.span
          className="w-2 h-2 rounded-full bg-red-500"
          animate={{ scale: [1, 1.5, 1], opacity: [1, 0.6, 1] }}
          transition={{ duration: 1, repeat: Infinity }}
        />

        {/* Waveform */}
        <div className="flex items-center gap-0.5 h-5">
          {amplitudes.map((amp, i) => (
            <motion.span
              key={i}
              className="w-0.5 bg-red-500 rounded-full"
              animate={{ height: `${Math.max(15, Math.min(100, amp * 100))}%` }}
              transition={{ duration: 0.1 }}
              style={{ minHeight: 4 }}
            />
          ))}
        </div>

        {/* Timer */}
        <span
          className={cn(
            "text-[11px] font-mono tabular-nums",
            isHot ? "text-red-600 font-semibold" : "text-red-500",
          )}
        >
          {formatDuration(durationMs)}
        </span>

        <button
          type="button"
          onClick={onStop}
          aria-label="Parar gravação"
          className={cn(
            "ml-1 w-6 h-6 rounded-full flex items-center justify-center",
            "bg-red-500 text-white hover:bg-red-600 transition",
          )}
        >
          <Square className="w-3 h-3 fill-current" />
        </button>
      </motion.div>
    )
  }

  return (
    <motion.button
      type="button"
      onClick={onStart}
      disabled={disabled}
      aria-label="Gravar mensagem por voz"
      className={cn(
        "flex items-center justify-center w-9 h-9 rounded-lg",
        "bg-glass-card border border-glass-border",
        "text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-glass-hover",
        "transition-colors",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-blue)]",
      )}
      whileHover={{ scale: disabled ? 1 : 1.05 }}
      whileTap={{ scale: disabled ? 1 : 0.95 }}
    >
      <Mic className="w-4 h-4" />
    </motion.button>
  )
}
